import axios from 'axios';
import qs from 'qs';
import { ISessionService } from './session.service';
import { SessionMetadata } from '@/store';
import rp from 'request-promise';

export interface StationStatus {
  currentStream: string;
  elapsedTime: number;
  provider?: string;
  link?: string;
  author?: string;
  imageSrc?: string;
  duration: number;
}

export enum MelosStationType {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

export interface MelosTrack {
  id?: string;
  audio?: any;
  title: string;
  file_name?: string;
  fileName?: string;
  duration?: number;
  tags?: string[];
  language?: string;
  artist?: string;
  lastPlayed?: Date;
}

export interface MelosPodcast {
  _id?: string;
  id?: string;
  uri: string;
  title: string;
  tags?: string[];
  language?: string;
}

export enum MelosDeviceType {
  STANDALONE = 'standalone_radio',
  TRANSMITTER = 'transmitter',
}

export interface MelosDevice {
  _id?: string;
  device_id?: string;
  name: string;
  type: MelosDeviceType;
  ip?: string;
}

export enum MelosContentType {
  FIXED = 'FIXED',
  TAG_BASED = 'TAG_BASED',
  USER_BASED = 'USER_BASED',
}

export interface MelosSlotContent {
  provider: string;
  tag?: string;
  name?: string;
  type?: MelosContentType;
  weight?: number;
}

export enum MelosContentOrder {
  FIXED = 'FIXED',
  WEIGHT_BASED = 'WEIGHT_BASED',
  RANDOM = 'RANDOM',
}

export interface MelosSlot {
  content: MelosSlotContent[];
  content_order: MelosContentOrder;
  start: string;
  end: string;
}

export interface MelosStation {
  _id?: string;
  owner?: string;
  name: string;
  type: MelosStationType;
  period_config: MelosSlot[];
  language?: string;
  interruption_config?: any;
  show_to?: string[];
}

export interface IApiService {
  getTracks(): Promise<MelosTrack[]>;
  getStations(): Promise<MelosStation[]>;
  getDevices(): Promise<MelosStation[]>;
  getPodcasts(): Promise<MelosPodcast[]>;
  getPublicStations(): Promise<MelosStation[]>;
  tuneStation(stationId: string): Promise<void>;
  tunePublicStation(stationId: string): Promise<void>;
  getStationWaveform(stationId: string): Promise<any>;
  getStationStatus(stationId: string): Promise<StationStatus>;
  getPublicStationWaveform(stationId: string): Promise<any>;
  getPublicStationStatus(stationId: string): Promise<StationStatus>;
  signIn(username: string, password: string, remember: boolean): Promise<void>;
  refreshSession(): Promise<void>;
  uploadTrack(track: MelosTrack, file: any): any;
  updateTrack(track: MelosTrack): any;
  updatePodcast(podcast: MelosPodcast): any;
  updateDevice(device: MelosDevice): any;
  createStation(station: MelosStation): any;
  registerDevice(device: MelosDevice): any;
  addPodcast(podcast: MelosPodcast): any;
  updateStation(station: MelosStation): any;
  removeTrack(trackId: string): any;
  removeStation(stationId: string): any;
  removePodcast(podcastId: string): any;
  removeDevice(deviceId: string): any;
  removeTrackCover(trackId: string): Promise<any>;
  removeStationLogo(trackId: string): Promise<any>;
  removeStationTheme(trackId: string): Promise<any>;
  uploadTrackCover(trackId: string, file: any): Promise<any>;
  fetchTrackCover(trackId: string): Promise<any>;
  uploadStationLogo(stationId: string, file: any): Promise<any>;
  uploadStationTheme(stationId: string, file: any): Promise<any>;
  getThemeWaveform(stationId: string): Promise<any>;
  getThemeDuration(stationId: string): Promise<any>;
  changePassword(username: string, currentPassword: string, newPassword: string): Promise<any>;
}

export class ApiService implements IApiService {
  private axiosInstance: any;
  private refreshInterval: any;

  constructor(
    private sessionService: ISessionService,
    private store: any,
    private router: any,
  ) {

    this.axiosInstance = axios.create({
      baseURL: 'https://api.melos.info',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.refreshSession();
  }

  public getTracks(): Promise<MelosTrack[]> {
    return this.authenticatedRequest('get', `/api/0.1/track`);
  }

  public getStations(): Promise<MelosStation[]> {
    return this.authenticatedRequest('get', `/api/0.1/station`);
  }

  public getDevices(): Promise<MelosStation[]> {
    return this.authenticatedRequest('get', `/api/0.1/user/devices`);
  }

  public getPodcasts(): Promise<MelosPodcast[]> {
    return this.authenticatedRequest('get', `/api/0.1/podcast`);
  }

  public getPublicStations(): Promise<MelosStation[]> {
    return this.anonymousRequest('get', `/api/0.1/station/public`);
  }

  public tuneStation(stationId: string): Promise<void> {
    return this.authenticatedRequest('get', `/api/0.1/station/${stationId}`);
  }

  public getStationWaveform(stationId: string): Promise<any> {
    return this.authenticatedRequest('get', `/stream/${stationId}/waveform`);
  }

  public getStationStatus(stationId: string): Promise<StationStatus> {
    return this.authenticatedRequest('get', `/stream/${stationId}/status`)
    .then((data: any) => data?.status || {currentStream: '', elapsedTime: 0});
  }

  public getPublicStationWaveform(stationId: string): Promise<any> {
    return this.anonymousRequest('get', `/stream/public/${stationId}/waveform`);
  }

  public getPublicStationStatus(stationId: string): Promise<StationStatus> {
    return this.anonymousRequest('get', `/stream/public/${stationId}/status`)
    .then((data: any) => data?.status || {currentStream: '', elapsedTime: 0});
  }

  public tunePublicStation(stationId: string): Promise<void> {
    return this.anonymousRequest('get', `/api/0.1/station/public/${stationId}`);
  }

  public uploadTrack(track: MelosTrack, file: any): any {
    const data = (Object.keys(track).reduce((currentData: any, key: any) => {
      currentData.append(key, (track as any)[key]);
      return currentData;
    }, new FormData()));
    data.append('audio', new Blob([file]));
    return this.authenticatedRequest('post', '/api/0.1/track', data, {
      headers: {
        'Authorization': `Bearer ${this.getBearerToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public changePassword(username: string, currentPassword: string, newPassword: string): Promise<any> {
    const data = {
      username,
      password: currentPassword,
      new_password: newPassword,
    };
    return this.authenticatedRequest('put', '/api/0.1/user/password', data);
  }

  public uploadTrackCover(trackId: string, file: any): any {
    const data = new FormData();
    data.append('image', new Blob([file]));
    return this.authenticatedRequest('post', `/api/0.1/track/${trackId}/cover`, data, {
      headers: {
        'Authorization': `Bearer ${this.getBearerToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public fetchTrackCover(trackId: string): any {
    return this.authenticatedRequest('post', `/api/0.1/track/${trackId}/fetch_cover`, {});
  }

  public getThemeWaveform(stationId: string): Promise<any> {
    return this.authenticatedRequest('get', `/api/0.1/station/${stationId}/theme/waveform`);
  }

  public async getThemeDuration(stationId: string): Promise<any> {
    const data = await this.authenticatedRequest('get', `/api/0.1/station/${stationId}/theme/duration`);
    return data.duration || 0;
  }

  public uploadStationLogo(stationId: string, file: any): any {
    const data = new FormData();
    data.append('logo', new Blob([file]));
    return this.authenticatedRequest('post', `/api/0.1/station/${stationId}/logo`, data, {
      headers: {
        'Authorization': `Bearer ${this.getBearerToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public uploadStationTheme(stationId: string, file: any): any {
    const data = new FormData();
    data.append('theme', new Blob([file]));
    return this.authenticatedRequest('post', `/api/0.1/station/${stationId}/theme`, data, {
      headers: {
        'Authorization': `Bearer ${this.getBearerToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public updateTrack(track: MelosTrack): any {
    return this.authenticatedRequest('put', `/api/0.1/track/${track.id}`, track);
  }

  public updatePodcast(podcast: MelosPodcast): any {
    return this.authenticatedRequest('put', `/api/0.1/podcast/${podcast._id}`, podcast);
  }

  public updateDevice(device: MelosDevice): any {
    return this.authenticatedRequest('put', `/api/0.1/device/${device._id}`, device);
  }

  public removeTrackCover(trackId: string): Promise<any> {
    return this.authenticatedRequest('delete', `/api/0.1/track/${trackId}/cover`);
  }

  public removeStationLogo(stationId: string): Promise<any> {
    return this.authenticatedRequest('delete', `/api/0.1/station/${stationId}/logo`);
  }

  public removeStationTheme(stationId: string): Promise<any> {
    return this.authenticatedRequest('delete', `/api/0.1/station/${stationId}/theme`);
  }

  public createStation(station: MelosStation): any {
    return this.authenticatedRequest('post', '/api/0.1/station', station);
  }

  public registerDevice(device: MelosDevice): any {
    return this.authenticatedRequest('post', '/api/0.1/device', device);
  }

  public addPodcast(podcast: MelosPodcast): any {
    return this.authenticatedRequest('post', '/api/0.1/podcast', podcast);
  }

  public updateStation(station: MelosStation): any {
    const stationData = { ...station };
    stationData.language = stationData.language || undefined;
    stationData.interruption_config = stationData.interruption_config || undefined;
    stationData.show_to = stationData.show_to || undefined;
    stationData.period_config = stationData.period_config;
    return this.authenticatedRequest('put', `/api/0.1/station/${station._id}`, stationData);
  }

  public removeTrack(trackId: string): any {
    return this.authenticatedRequest('delete', `/api/0.1/track/${trackId}`);
  }

  public removeStation(stationId: string): any {
    return this.authenticatedRequest('delete', `/api/0.1/station/${stationId}`);
  }

  public removePodcast(podcastId: string): any {
    return this.authenticatedRequest('delete', `/api/0.1/podcast/${podcastId}`);
  }

  public removeDevice(deviceId: string): any {
    return this.authenticatedRequest('delete', `/api/0.1/device/${deviceId}`);
  }

  public async signIn(username: string, password: string, remember: boolean = false): Promise<void> {
    return this.oauthRequest(
      'post',
      '/oauth/token',
      { username, password, grant_type: 'password', client_id: 'client', client_secret: 'test' },
    )
    .then((respData) => {
      this.sessionService.setPersistance(remember);
      this.sessionService.renewSession({
        token: respData.access_token,
        refreshToken: respData.refresh_token,
        username,
        userId: '',
        version: SessionMetadata.VERSION,
      });
    });
  }

  public async refreshSession(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return;
    }
    return this.oauthRequest(
      'post',
      '/oauth/token',
      { refresh_token: refreshToken, grant_type: 'refresh_token', client_id: 'client', client_secret: 'test' },
    )
    .then((respData) => {
      const currentSessionData = this.sessionService.getSessionData();
      this.sessionService.renewSession({
        token: respData.access_token,
        refreshToken: respData.refresh_token,
        username: currentSessionData.username,
        userId: currentSessionData.userId,
        version: SessionMetadata.VERSION,
      });
      this.startRefreshInterval();
    })
    .catch(() => {
      return this.signOut();
    });
  }

  public async signOut(): Promise<void> {
    return Promise.resolve()
    .then(() => {
      this.sessionService.clearSession();
      this.router.push({ path: '/login' });
    });
  }

  private getRefreshToken(): string|null {
    try {
      return this.sessionService.getSessionData().refreshToken;
    } catch (e) {
      return null;
    }
  }

  private startRefreshInterval() {
    if (this.refreshSession !== undefined) {
      clearInterval(this.refreshInterval);
    }
    this.refreshInterval = setInterval(() => {
      this.refreshSession();
    }, this.getRefreshDelay());
  }

  private getRefreshDelay(): number {
    return this.sessionService.getSessionTime() * 0.8;
  }

  private getBearerToken(): string {
    return this.sessionService.getSessionData().token;
  }

  private async oauthRequest(method: string, url: string, data: any = {}): Promise<any> {
    const req = {
      method,
      url,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: qs.stringify(data),
    };
    return this.axiosInstance.request(req)
    .then((resp: any) => resp.data);
  }

  private async anonymousRequest(method: string, url: string, data: any = {}, opts: any = {}): Promise<any> {
    const req = { method, url, data, ...opts };
    return this.axiosInstance.request(req)
    .then((resp: any) => resp.data);
  }

  private async checkAccessToken(): Promise<void> {
    return this.sessionService.sessionExpired() ? this.refreshSession() : Promise.resolve();
  }

  private async authenticatedRequest(method: string, url: string, data: any = {}, opts: any = {}): Promise<any> {
    return this.checkAccessToken()
    .then(() => {
      return this.axiosInstance.request({
        method,
        url,
        data,
        headers: { Authorization: `Bearer ${this.getBearerToken()}` },
        ...opts,
      });
    })
    .then((resp: any) => resp.data)
    .catch((error: any) => {
      this.handleError(error);
    });
  }

  private handleError(error: any) {
    switch (error?.response?.status) {
      case 401:
        this.signOut();
        break;
      default:
        break;
    }
  }
}
